import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import type { Strategies } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'
import { strategyKeys } from '@/react-query/queries/strategies'

const invalidateStrategies = (client: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    client.invalidateQueries({ queryKey: strategyKeys.all }),
    client.invalidateQueries({ queryKey: ['static', 'trade-strategies'] }),
    client.invalidateQueries({ queryKey: ['static/strategies'] }),
  ])

export const useCreateStrategyMutation = () => {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: (params: Strategies.CreateParams) => api.strategies.create(params),
    onSuccess: () => toast.success('Strategy created'),
    onError: (error) => toastApiError(error, 'Unable to create strategy'),
    // Insert can commit before logging/enrichment fails.
    onSettled: async () => {
      await invalidateStrategies(client)
    },
  })
}

export const useUpdateStrategyMutation = () => {
  const api = useApi()
  const client = useQueryClient()

  return useMutation({
    retry: false,
    mutationFn: (params: Strategies.UpdateParams) => api.strategies.update(params),
    onSuccess: () => toast.success('Strategy updated'),
    onError: (error) => toastApiError(error, 'Unable to update strategy'),
    // Update can commit before logging/readback fails.
    onSettled: async () => {
      await invalidateStrategies(client)
    },
  })
}
